import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getLifebooksChild, getLifebooksChildrenSort } from 'redux/selectors';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { experiments } from 'helpers';
import useManageChildAdminPermissions from '../../utils/useManageChildAdminPermissions';
import * as ChildComp from '.';
import { SectionHeader } from 'components';
import clsx from 'clsx';
import styles from './ChildAdmin.module.css';

export function ChildAdmin() {
	const permissions = useManageChildAdminPermissions();
	const child = useSelector(getLifebooksChild);
	const user = useSelector((state) => state.user.user.type);

	const { childId } = useParams();
	const childSort = useSelector(getLifebooksChildrenSort);

	const [expanded, setExpanded] = useState(false);

	const inviteLabel = () => {
		if (child.status === 'active') {
			return 'Active';
		} else if (child.status === 'invited') {
			return 'Resend Invite';
		} else {
			return 'Invite';
		}
	};

	const buttonArray = childId
		? [
				permissions.inviteChild && {
					label: 'Invite Child',
					info: 'This option will invite the child. Must have email saved above.',
					button: (
						<ChildComp.InviteChildButton
							child={child}
							childId={childId}
							label={inviteLabel()}
						/>
					),
				},
				permissions.exitCare && {
					label: 'Exiting Care',
					info: 'Use this option if a child has exited care or is no longer managed by your agency. ',
					button: (
						<ChildComp.ExitCareButton childId={childId} childSort={childSort} />
					),
				},
				permissions.transferCare && {
					label: 'Transfer to Another Ella Agency',
					info: 'Send this LifeBook to another agency on Ella. They accept, then you release it to finish.',
					button: <ChildComp.TransferChildButton child={child} />,
				},
				permissions.deleteChild && {
					label: 'Delete Child',
					info: 'This option will permanently delete this child from the Ella system.',
					warn: true,
					button: (
						<ChildComp.DeleteChildButton
							child={child}
							childId={childId}
							childSort={childSort}
						/>
					),
				},
			]
		: [];

	const hasItems = () => {
		return childId && Object.values(permissions).some((val) => !!val);
	};

	const buttonMapper = (val) => {
		if (val) {
			const { label, info, button } = val;
			return (
				<div
					className={clsx(
						styles.childAdminItemWrapper,
						val.warn ? styles.childAdminWarn : '',
					)}
					key={label}>
					<div>
						<div className={styles.childAdminBoldLabel}>{label}</div>
						<div>{info}</div>
					</div>
					<div className={styles.childAdminButtonContainer}>{button}</div>
				</div>
			);
		} else return null;
	};

	const handleOpenClose = () => {
		setExpanded(!expanded);
	};

	return hasItems() ? (
		<div className={styles.childAdminRoot}>
			<div className={styles.childAdminContent}>
				<SectionHeader hideTop>
					<div className={styles.basicHeader}>
						<div className={styles.headerText}>MANAGEMENT OPTIONS</div>
						{expanded ? (
							<ExpandLess onClick={() => handleOpenClose()} />
						) : (
							<ExpandMore onClick={() => handleOpenClose()} />
						)}
					</div>
				</SectionHeader>
				{expanded && (
					<>
						{permissions.exportPdf && <ChildComp.ExportPdf type={'pdf'} />}
						{permissions.exportPdf && !child.privatizePosts && (
							<ChildComp.ExportPdf type={'zip'} />
						)}
						{experiments.familyConnect(true) && user === 'agencyuser' && (
							<ChildComp.ConnectChildButton childId={childId}>
								Connect Child
							</ChildComp.ConnectChildButton>
						)}
						{buttonArray.map(buttonMapper)}
					</>
				)}
			</div>
		</div>
	) : null;
}

export default ChildAdmin;
